import { useState } from "react";
import { toast } from "sonner";

const AbsenceRequestModal = ({ darkMode, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    absence_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const absenceTypes = [
    { value: 'vacation', label: 'Férias' },
    { value: 'sick_leave', label: 'Baixa Médica' },
    { value: 'personal', label: 'Pessoal' },
    { value: 'parental', label: 'Parental' },
    { value: 'bereavement', label: 'Luto' },
    { value: 'wedding', label: 'Casamento' },
    { value: 'study', label: 'Estudos' },
    { value: 'unpaid', label: 'Não Remunerada' },
    { value: 'other', label: 'Outro' }
  ];

  const calculateDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date || !formData.end_date) {
      toast.error('Indique as datas de início e fim');
      return;
    }
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('Data de fim deve ser posterior à data de início');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({
        ...formData,
        total_days: calculateDays()
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`rounded-xl border w-full max-w-lg ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} onClick={e => e.stopPropagation()}>
        <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Novo Pedido de Ausência</h3>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Solicite férias ou outro tipo de ausência</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Tipo de Ausência *</label>
              <select
                value={formData.absence_type}
                onChange={e => setFormData({...formData, absence_type: e.target.value})}
                className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                required
              >
                {absenceTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Data Início *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={e => setFormData({...formData, start_date: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                  required
                  data-testid="absence-start-date"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Data Fim *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={e => setFormData({...formData, end_date: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                  required
                  data-testid="absence-end-date"
                />
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  Total: <strong>{calculateDays()}</strong> dias
                </span>
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Motivo</label>
              <textarea
                value={formData.reason}
                onChange={e => setFormData({...formData, reason: e.target.value})}
                className={`w-full px-3 py-2 rounded-lg border resize-none ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                rows={3}
                placeholder="Descreva brevemente o motivo da ausência (opcional)"
                data-testid="absence-reason"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Notas Adicionais</label>
              <input
                type="text"
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}`}
                placeholder="Informações adicionais"
              />
            </div>
          </div>
          
          <div className={`p-6 border-t flex justify-end gap-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={submitting}
              data-testid="submit-absence-btn"
            >
              {submitting ? 'A submeter...' : 'Submeter Pedido'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AbsenceRequestModal;
